import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Sparkles, CalendarRange, BarChart3, Users, Mic2, Library, Check, ArrowRight, Gift, Music, Disc3, Headphones } from "lucide-react";
import CompareTable from "../components/CompareTable";
import { useCurrency } from "@/lib/currency";
import { CurrencyPicker } from "@/components/CurrencyPicker";

const HERO_BG = "https://images.unsplash.com/photo-1476136236990-838240be4859?crop=entropy&cs=srgb&fm=jpg&q=85";
const DJ_IMG = "https://images.pexels.com/photos/743715/pexels-photo-743715.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

// Anonymous visitor id (persists per browser) — used by CTA click tracker
const getAnonId = () => {
  try {
    let id = localStorage.getItem("bfa_anon_id");
    if (!id) {
      id = "anon_" + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
      localStorage.setItem("bfa_anon_id", id);
    }
    return id;
  } catch { return "anon_nostorage"; }
};

// Fire-and-forget landing-page CTA click counter. Never blocks navigation.
const trackCta = (cta) => {
  try {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/landing/cta-click`;
    const body = JSON.stringify({ cta, anon_id: getAnonId(), referrer: document.referrer || "" });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
    } else {
      fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body, keepalive: true }).catch(() => {});
    }
  } catch { /* swallow — never block the link */ }
};

const PLATFORMS = ["Facebook", "Instagram", "X", "TikTok", "YouTube", "LinkedIn", "WhatsApp"];

const FEATURES = [
  { icon: Sparkles, title: "AI Content Studio", desc: "Generate artist bios, captions, podcast titles, descriptions, and SEO hashtags powered by Claude Sonnet 4.5." },
  { icon: CalendarRange, title: "Multi-platform Scheduler", desc: "Plan posts across Facebook, Instagram, X, TikTok, YouTube, LinkedIn & WhatsApp from one drag-and-drop calendar." },
  { icon: Mic2, title: "Podcast Packaging", desc: "Auto-generate show notes, chapter markers, summaries, and hashtags for every episode you publish." },
  { icon: Library, title: "Unified Library", desc: "Every generation, every episode, every caption — searchable, taggable, never lost again." },
  { icon: BarChart3, title: "Broadcast Analytics", desc: "Track AI usage, posts published, and platform breakdown with a control-room dashboard." },
  { icon: Users, title: "Roles for the Whole Crew", desc: "Station Owner, DJ, Social Media Manager — each role gets the right tools, nothing more." },
];

// Spec: /app/customer-assets/compare_plans_build_prompt.md (Feb 2026).
// Complete feature-to-tier map. Each `groups` entry renders as a
// labeled subsection inside the pricing card so long tiers stay
// scannable. The first group's `label` is null → renders without a
// divider. Additional groups render "// LABEL" mono headers.
const PLANS = [
  {
    id: "free",
    name: "Free",
    price: 0,
    subtitle: "Try it with one station",
    priceSuffix: "forever",
    ctaLabel: "Start free →",
    groups: [
      { label: "Core", features: [
        "1 station",
        "50 AI credits / mo",
        "Control Room & Station settings",
        "BroadcastFlow watermark on exports",
        "Community support",
        "No credit card required",
      ]},
    ],
  },
  {
    id: "starter",
    name: "Starter",
    price: 29,
    subtitle: "Get content out the door",
    ctaLabel: "Subscribe monthly",
    groups: [
      { label: "Everything in Free, plus", features: [
        "300 AI credits / mo",
        "Basic AI content tools",
        "Basic social posts",
        "WordPress publishing",
        "Basic analytics",
      ]},
      { label: "Data & Content", features: [
        "News Ingestion & RSS Feeds",
        "Blog Connector",
        "Content Library",
        "Now Playing (display)",
        "Transcribe capped hrs/mo",
      ]},
    ],
  },
  {
    id: "professional",
    name: "Professional",
    price: 99,
    subtitle: "Real AI content generation",
    ctaLabel: "Subscribe monthly",
    groups: [
      { label: "Everything in Starter, plus", features: [
        "1,500 AI credits / mo",
        "AI DJ Assistant",
        "Social Scheduler",
        "Now-Playing Auto Poster",
        "Advanced analytics",
        "3 team members",
      ]},
      { label: "Content Generation", features: [
        "AI News Center / News Studio",
        "Podcast Studio (Vault + Creator)",
        "Top Charts (Apple / Last.fm / Deezer)",
        "Quick Promo Creator",
        "AI Music Studio instrumental",
        "Schedule",
        "Academy",
      ]},
    ],
  },
  {
    id: "pro_radio",
    name: "Pro Radio",
    price: 299,
    subtitle: "Full production & voice suite",
    highlight: true,
    ctaLabel: "Subscribe monthly",
    groups: [
      { label: "Everything in Professional, plus", features: [
        "5,000 AI credits / mo",
        "Voice tracking",
        "AI Commercial Creator",
        "Team accounts (10 members)",
        "Advanced reporting",
        "Priority support",
      ]},
      { label: "Studio & Voice", features: [
        "AI News Anchor / Video News Studio",
        "Radio Imaging Studio",
        "Vision Studio, Audio Studio, AI Avatar Studio",
        "Stem Separation Studio",
        "AI Vocalist Studio — 30s cap · BYOK",
        "Karaoke Studio",
        "Production Suite (DAW-style)",
        "Artist Drops",
        "Push to Air & Program Guide",
        "AI Providers (BYOK key management)",
      ]},
    ],
  },
  {
    id: "network",
    name: "Network",
    price: 499,
    subtitle: "Run your whole cluster",
    badge: "For Multi-Station Groups",
    ctaLabel: "Subscribe monthly",
    groups: [
      { label: "Everything in Pro Radio, plus", features: [
        "15,000 AI credits / mo",
        "Unlimited stations",
        "24/7 success manager",
      ]},
      { label: "Multi-Station Only", features: [
        "Network analytics",
        "Shared content libraries",
        "White-label option",
        "Multi-station publishing",
        "Centralized newsroom",
        "Station brand voice",
        "Org-wide Integrations & Social Connections",
      ]},
    ],
  },
];

export default function Landing() {
  const [referrer, setReferrer] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  const [proCount, setProCount] = useState(null);
  // Update #184 · yearly/monthly toggle · 20% annual discount
  const [billingCycle, setBillingCycle] = useState("monthly");   // "monthly" | "yearly"
  const { currency, setCurrency, fmt: fx, isUsd } = useCurrency();

  // Fetch the current Pro-tier station count for the pricing social-proof
  // ticker. Public endpoint, cached server-side for 5 min.
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pricing/pro-signup-count`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d?.count) setProCount(d.count); })
      .catch(() => {});
  }, []);

  // Capture ?ref=CODE on first load: stash in localStorage so the
  // AuthCallback can submit it during signup, then validate against the
  // backend to surface a "you'll get +N credits" teaser banner.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get("ref") || "").trim().toUpperCase();
      if (ref) {
        localStorage.setItem("bfa_ref_code", ref);
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/check/${encodeURIComponent(ref)}`)
          .then((r) => r.ok ? r.json() : null)
          .then((d) => { if (d?.valid) setReferrer(d); })
          .catch(() => {});
      } else {
        // Fall back to a previously stashed code (e.g. user navigated away & back)
        const cached = localStorage.getItem("bfa_ref_code");
        if (cached) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/check/${encodeURIComponent(cached)}`)
            .then((r) => r.ok ? r.json() : null)
            .then((d) => { if (d?.valid) setReferrer(d); })
            .catch(() => {});
        }
      }
    } catch { /* no-op */ }
  }, []);

  return (
    <div className="min-h-screen bg-bg text-ink-primary">
      {/* Referral banner — only shows when ?ref=CODE matched a real user */}
      {referrer && (
        <div
          className="bg-gradient-to-r from-brand-cyan/15 to-brand-amber/10 border-b border-brand-cyan/30"
          data-testid="landing-referral-banner"
        >
          <div className="mx-auto max-w-7xl px-6 py-2.5 flex items-center justify-center gap-3 text-sm">
            <Gift className="h-4 w-4 text-brand-amber" />
            <span className="text-ink-primary">
              <strong>{referrer.referrer_first_name}</strong> invited you to BroadcastFlow AI — sign up and you'll <strong className="text-brand-amber">both</strong> get <strong className="text-brand-amber">{referrer.bonus_per_referral} bonus credits</strong>.
            </span>
          </div>
        </div>
      )}

      {/* Nav */}
      <nav className="sticky top-0 z-40 border-b border-line bg-bg/70 backdrop-blur-xl" data-testid="landing-nav">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="flex items-center" data-testid="landing-logo">
            <img src="/brand/broadcastflowai-hero.png" alt="BroadcastFlow AI" className="h-16 w-auto object-contain" />
          </Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-ink-secondary">
            <a href="#features" className="hover:text-ink-primary transition">Features</a>
            <a href="#platforms" className="hover:text-ink-primary transition">Platforms</a>
            <Link
              to="/music-showcase"
              data-testid="nav-music-studio"
              onClick={() => trackCta("nav-music-studio")}
              className="relative group flex items-center gap-1.5 text-fuchsia-300 hover:text-fuchsia-200 transition"
            >
              <Music className="h-3.5 w-3.5" />
              <span>AI Music Studio</span>
              <span className="absolute -top-2 -right-3 px-1 py-px rounded-full bg-fuchsia-500/20 border border-fuchsia-400/50 text-[8px] font-mono uppercase tracking-wider text-fuchsia-200">New</span>
            </Link>
            <a href="#pricing" className="hover:text-ink-primary transition">Pricing</a>
          </div>
          <Link
            to="/login"
            data-testid="landing-cta-signin"
            className="rounded-md bg-brand-amber px-5 py-2 text-sm font-semibold text-black hover:bg-brand-amberHover amber-glow"
          >
            Sign in
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-bg/70 via-bg/85 to-bg" />
        <div className="absolute inset-0 scan-line" />
        <div className="relative mx-auto max-w-7xl px-6 pt-24 pb-32">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              {/* BIG BRANDED LOGO */}
              <div className="mb-8" data-testid="hero-brand">
                <img
                  src="/brand/broadcastflowai-hero.png"
                  alt="BroadcastFlow AI"
                  className="h-32 sm:h-40 w-auto object-contain drop-shadow-[0_8px_32px_rgba(255,138,31,0.35)]"
                />
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-300/80 mt-3">// BROADCAST AUTOMATION OS</div>
              </div>

              <div className="inline-flex items-center gap-2 rounded-full border border-line bg-bg-surface/60 px-3 py-1 mb-6">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-onair onair-dot" />
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink-secondary">
                  Live // Hands-free AI content for broadcasters
                </span>
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
                Hands-free<br />
                <span className="text-brand-amber">broadcast automation.</span>
              </h1>
              <p className="mt-6 max-w-xl text-base sm:text-lg text-ink-secondary leading-relaxed">
                One platform that ingests, writes, voices, mixes, renders &amp; schedules every news bulletin, social post, blog article, podcast trailer and on-air imaging spot — for your radio station.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Link
                  to="/login"
                  data-testid="hero-cta-start"
                  className="group inline-flex items-center gap-2 rounded-md bg-brand-amber px-6 py-3 font-semibold text-black amber-glow"
                >
                  Start broadcasting smarter
                  <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                </Link>
                <a
                  href="#features"
                  className="rounded-md border border-line bg-bg-elevated px-6 py-3 font-medium text-ink-primary hover:bg-bg-surface transition"
                  data-testid="hero-cta-explore"
                >
                  Explore the studio
                </a>
              </div>
              <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs text-ink-muted font-mono uppercase tracking-wider">
                <div className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-brand-amber" />
                  Claude Sonnet 4.5
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-brand-amber" />
                  12 Platforms
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-brand-amber" />
                  WordPress · Reels
                </div>
                <div className="flex items-center gap-2">
                  <Check className="h-3.5 w-3.5 text-brand-amber" />
                  No credit card
                </div>
              </div>
            </div>
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-2xl border border-line bg-bg-surface p-6 lift">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-full bg-brand-onair onair-dot" />
                      <span className="h-2.5 w-2.5 rounded-full bg-line" />
                      <span className="h-2.5 w-2.5 rounded-full bg-line" />
                    </div>
                    <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted ml-2">ai-studio.live</span>
                  </div>
                  <span className="font-mono text-[10px] text-brand-amber">CLAUDE-4.5</span>
                </div>
                <div className="font-mono text-xs text-ink-secondary space-y-2">
                  <div className="text-brand-amber">&gt; news-studio render --voice=female_news --duration=30s</div>
                  <div className="text-ink-primary leading-relaxed">
                    📻 Top of the hour — Kylie Minogue is brushing up on her AFL skills, plus Drake teases album 9 and the Grammys announce host. More at the top of the hour on Seductively Silky Radio.<br /><br />
                    <span className="text-cyan-300">✓ MP3 mixed · ✓ Reel rendered · ✓ WP draft created · ✓ 5 social posts queued</span>
                  </div>
                  <div className="text-ink-muted">// 1 story → 9 outputs in 22s</div>
                  <div className="text-brand-amber caret"></div>
                </div>
                <div className="mt-4 grid grid-cols-7 gap-1.5">
                  {PLATFORMS.map((p, i) => (
                    <div
                      key={p}
                      className="h-1.5 rounded-full bg-brand-amber/30"
                      style={{ animation: `wave ${1 + i * 0.1}s ease-in-out infinite`, transformOrigin: "bottom" }}
                    />
                  ))}
                </div>
              </div>
              <div className="absolute -bottom-6 -right-6 hidden lg:block rounded-xl border border-line bg-bg-elevated p-4 w-48 lift">
                <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">On Air</div>
                <div className="font-display text-lg font-bold mt-1">DJ Nova</div>
                <div className="text-xs text-brand-amber">Studio 7 — Live</div>
                <div className="mt-3 flex items-end gap-0.5 h-6">
                  {[0.6, 0.9, 0.4, 0.8, 0.5, 1.0, 0.7, 0.3, 0.85, 0.6].map((h, i) => (
                    <div
                      key={i}
                      className="flex-1 bg-brand-amber wave-bar"
                      style={{ height: `${h * 100}%`, animationDelay: `${i * 0.08}s` }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms strip */}
      <section id="platforms" className="border-y border-line bg-bg-surface py-12">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center font-mono text-[10px] uppercase tracking-[0.25em] text-ink-muted mb-6">
            One studio · Seven platforms · Infinite reach
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-4">
            {PLATFORMS.map((p) => (
              <span key={p} className="font-display text-2xl sm:text-3xl font-bold text-ink-secondary hover:text-cyan-300 transition cursor-default" data-testid={`platform-${p.toLowerCase()}`}>
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ──────────────────────────────────────────────────────────────
          MUSIC STUDIO TEASER — distinct fuchsia/purple aesthetic
          breaks away from the amber/cyan house style to signal this is a
          premium new module. Deep linkable via #music-studio.
         ────────────────────────────────────────────────────────────── */}
      <section
        id="music-studio"
        data-testid="landing-music-teaser"
        className="relative border-y border-fuchsia-500/20 bg-gradient-to-br from-[#0c0817] via-[#150a26] to-[#0c0817] py-20 sm:py-28 overflow-hidden"
      >
        {/* glow blobs */}
        <div className="pointer-events-none absolute -top-32 -left-24 w-96 h-96 rounded-full bg-fuchsia-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-40 -right-24 w-[28rem] h-[28rem] rounded-full bg-purple-500/15 blur-3xl" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.07]"
             style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "32px 32px" }} />

        <div className="relative mx-auto max-w-7xl px-6 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 rounded-full border border-fuchsia-400/40 bg-fuchsia-500/10 px-3 py-1 mb-5">
              <Disc3 className="h-3.5 w-3.5 text-fuchsia-300 animate-spin-slow" style={{ animationDuration: "8s" }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-fuchsia-200">
                New // Suno-powered AI music production
              </span>
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02]">
              Your station&apos;s own
              <br />
              <span className="bg-gradient-to-r from-fuchsia-300 via-pink-300 to-amber-300 bg-clip-text text-transparent">
                music supervisor.
              </span>
            </h2>
            <p className="mt-5 max-w-xl text-base sm:text-lg text-ink-secondary leading-relaxed">
              Compose unique station themes, news beds, podcast intros, holiday songs and contest jingles in seconds.
              16 broadcaster templates. BYOK Suno integration. Royalty-free output you own.
            </p>

            <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2.5 max-w-2xl">
              {[
                { icon: Radio, label: "Station Themes" },
                { icon: Headphones, label: "Show Beds" },
                { icon: Mic2, label: "Podcast Music" },
                { icon: Sparkles, label: "Jingles & FX" },
              ].map((t) => (
                <div
                  key={t.label}
                  className="rounded-lg border border-fuchsia-400/20 bg-white/[0.02] backdrop-blur px-3 py-2.5 flex items-center gap-2"
                  data-testid={`music-teaser-pill-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <t.icon className="h-4 w-4 text-fuchsia-300 shrink-0" />
                  <span className="text-xs font-medium text-ink-primary">{t.label}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to="/music-showcase"
                data-testid="music-teaser-cta-primary"
                onClick={() => trackCta("music-teaser-showcase")}
                className="group inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-fuchsia-500/30 transition hover:-translate-y-0.5 hover:shadow-fuchsia-500/50"
                style={{ background: "linear-gradient(135deg, #d946ef 0%, #a855f7 50%, #f59e0b 100%)" }}
              >
                <Music className="h-4 w-4" />
                Listen to the Showcase
                <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
              </Link>
              <Link
                to="/login"
                data-testid="music-teaser-cta-secondary"
                onClick={() => trackCta("music-teaser-start")}
                className="inline-flex items-center gap-2 rounded-md border border-fuchsia-400/40 bg-fuchsia-500/5 px-5 py-3 text-sm font-medium text-fuchsia-100 hover:bg-fuchsia-500/10 transition"
              >
                Start producing
              </Link>
            </div>
          </div>

          {/* Right column: animated equalizer / vinyl */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square max-w-md mx-auto rounded-3xl border border-fuchsia-400/20 bg-gradient-to-br from-fuchsia-900/30 via-purple-900/20 to-amber-900/20 p-8 flex items-center justify-center backdrop-blur-xl">
              {/* Vinyl */}
              <div className="relative w-full aspect-square rounded-full border-4 border-fuchsia-300/30 shadow-2xl shadow-fuchsia-500/20"
                   style={{ background: "radial-gradient(circle at center, #0a0014 8%, #1a0a28 14%, #0a0014 16%, #150823 26%, #0a0014 30%, #1a0a28 42%, #0a0014 46%, #150823 56%, #0a0014 60%, #1a0a28 72%, #0a0014 76%, #150823 88%, #0a0014 92%)",
                            animation: "spin 24s linear infinite" }}>
                <div className="absolute inset-[42%] rounded-full bg-gradient-to-br from-fuchsia-400 to-amber-400 flex items-center justify-center">
                  <Music className="h-1/2 w-1/2 text-bg" />
                </div>
              </div>
              {/* Equalizer bars over the vinyl */}
              <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex items-end gap-1.5 h-10">
                {[0.4, 0.7, 0.9, 0.6, 1.0, 0.5, 0.8, 0.45].map((h, i) => (
                  <div key={i} className="w-1.5 rounded-full bg-gradient-to-t from-fuchsia-400 to-amber-300 wave-bar"
                       style={{ height: `${h * 100}%`, animationDelay: `${i * 0.08}s` }} />
                ))}
              </div>
              {/* Floating chip */}
              <div className="absolute -top-3 -right-3 rounded-full border border-fuchsia-400/40 bg-bg/80 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-fuchsia-200">
                Suno · v3.5
              </div>
              <div className="absolute -bottom-4 -left-4 rounded-md border border-amber-400/40 bg-bg/80 backdrop-blur px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-amber-200">
                16 Templates
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platforms strip — placeholder removed (kept above) */}

      {/* Features */}
      <section id="features" className="py-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="max-w-2xl mb-16">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-3">// FEATURES</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter">
              Every studio tool, in one mixing console.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className="rounded-xl border border-line bg-bg-surface p-6 lift"
                data-testid={`feature-${i}`}
              >
                <f.icon className="h-6 w-6 text-brand-amber mb-4" />
                <h3 className="font-display text-xl font-bold mb-2">{f.title}</h3>
                <p className="text-sm text-ink-secondary leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DJ section */}
      <section className="border-y border-line bg-bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-3">// FOR DJs & PRODUCERS</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05]">
              Less typing. More mixing.
            </h2>
            <p className="mt-6 text-ink-secondary leading-relaxed">
              You're behind the decks, not the keyboard. BroadcastFlow AI generates your show write-ups, artist bios, and social promos before your set even ends — so you can stay in the booth and let the algorithm handle the captions.
            </p>
            <ul className="mt-8 space-y-3">
              {["Per-platform tone & length tuning", "DJ profiles auto-attach to shows", "Shoutcast/Icecast stream URL ready", "Drag-and-drop schedule editor"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-sm">
                  <Check className="h-4 w-4 text-brand-amber" />
                  {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="relative">
            <img src={DJ_IMG} alt="DJ at console" className="rounded-2xl border border-line object-cover h-[480px] w-full grain" />
            <div className="absolute bottom-6 left-6 right-6 rounded-xl bg-bg/85 backdrop-blur-xl border border-line p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-brand-amber">Now generating</div>
                  <div className="font-display text-lg font-bold mt-0.5">Late Night Lounge — Ep. 42</div>
                </div>
                <span className="text-xs font-mono text-ink-muted">00:00:04</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-28">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-brand-amber mb-3">// PRICING</div>
            <h2 className="font-display text-4xl lg:text-5xl font-black tracking-tighter">
              Pick your frequency.
            </h2>
            <p className="mt-4 text-ink-secondary">Start free, forever. Scale up when you&apos;re ready to broadcast wider.</p>
          </div>

          {/* Free-tier banner — no CC required, one station, keep it forever */}
          <div
            data-testid="pricing-trial-banner"
            className="mx-auto max-w-3xl mb-6 rounded-xl border border-cyan-500/40 bg-cyan-500/5 px-5 py-4 flex flex-col sm:flex-row items-center justify-center gap-3 text-center sm:text-left"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-cyan-300 shrink-0">
              // FREE FOREVER
            </div>
            <div className="text-sm text-ink-primary">
              <span className="font-semibold text-cyan-300">Every station gets a permanent Free plan</span>
              <span className="text-ink-secondary"> — 1 station, 50 AI credits/month, no credit card required. Upgrade any time to unlock more credits, more studios, and unlimited stations.</span>
            </div>
          </div>

          {/* Billing-cycle toggle · Update #184 */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10" data-testid="pricing-cycle-toggle">
            <div className="inline-flex items-center rounded-full border border-line bg-bg-surface p-1 relative">
              <button
                type="button"
                onClick={() => setBillingCycle("monthly")}
                data-testid="pricing-cycle-monthly"
                className={`relative z-10 px-5 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.14em] transition ${
                  billingCycle === "monthly"
                    ? "bg-bg-elevated text-brand-amber shadow-sm"
                    : "text-ink-muted hover:text-ink-primary"
                }`}
              >
                Monthly
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle("yearly")}
                data-testid="pricing-cycle-yearly"
                className={`relative z-10 px-5 py-2 rounded-full font-mono text-[11px] uppercase tracking-[0.14em] transition ${
                  billingCycle === "yearly"
                    ? "bg-bg-elevated text-brand-amber shadow-sm"
                    : "text-ink-muted hover:text-ink-primary"
                }`}
              >
                Yearly
                <span className="ml-2 inline-block rounded-full bg-emerald-500/20 text-emerald-300 px-1.5 py-0.5 text-[9px] font-bold tracking-normal normal-case">
                  2 months free
                </span>
              </button>
            </div>
            <CurrencyPicker currency={currency} onChange={setCurrency} />
          </div>
          {!isUsd && (
            <div className="-mt-7 mb-10 text-center font-mono text-[10px] uppercase tracking-[0.15em] text-ink-muted" data-testid="landing-fx-note">
              ≈ shown in {currency} at today's rate — checkout is billed in USD
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 max-w-7xl mx-auto">
            {PLANS.map((p) => (
              <div
                key={p.id}
                className={`group relative rounded-2xl border bg-bg-surface p-6 lift flex flex-col ${
                  p.highlight
                    ? "border-brand-amber/60 lg:scale-[1.03] z-10"
                    : p.id === "network"
                    ? "border-brand-blue/40"
                    : "border-line"
                }`}
                style={
                  p.highlight
                    ? { background: "linear-gradient(180deg, rgba(255,138,31,0.06) 0%, rgba(20,20,28,1) 45%)" }
                    : p.id === "network"
                    ? { background: "linear-gradient(180deg, rgba(0,147,255,0.05) 0%, rgba(20,20,28,1) 50%)" }
                    : undefined
                }
                data-testid={`plan-${p.id}`}
              >
                {p.highlight && (
                  <div
                    data-testid={`plan-popular-badge-${p.id}`}
                    className="popular-pulse absolute -top-3 left-6 rounded-full bg-brand-amber px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-black font-bold"
                  >
                    Most Popular
                  </div>
                )}
                {/* Update #186 · hover tooltip in monthly mode — exact $ saved by going yearly */}
                {billingCycle === "monthly" && p.price > 0 && (
                  <button
                    type="button"
                    onClick={() => setBillingCycle("yearly")}
                    data-testid={`plan-savings-tooltip-${p.id}`}
                    className="absolute -top-3 right-4 rounded-full border border-emerald-400/40 bg-[#0e1a14] px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-emerald-300 opacity-0 translate-y-1 transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 hover:bg-emerald-500/20 z-20"
                  >
                    Save {fx(p.price * 2)}/yr → go yearly
                  </button>
                )}
                {p.badge && !p.highlight && (
                  <div
                    data-testid={`plan-network-badge-${p.id}`}
                    className="absolute -top-3 left-6 rounded-full bg-brand-blue/90 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-black font-bold"
                  >
                    {p.badge}
                  </div>
                )}
                <div className="font-display text-2xl font-bold">{p.name}</div>
                {(() => {
                  // Update #186 · yearly = 10× monthly (2 months free) — matches backend PLANS / Stripe prices exactly.
                  const isYearly = billingCycle === "yearly" && p.price > 0;
                  const annualTotal = isYearly ? p.price * 10 : null;
                  const monthlyEffective = isYearly ? Math.round((p.price * 10) / 12) : p.price;
                  const strikeMonthly = isYearly ? p.price : null;
                  const priceStr = fx(monthlyEffective);
                  const priceSize = priceStr.length > 8 ? "text-3xl" : priceStr.length > 5 ? "text-4xl" : "text-5xl";
                  return (
                    <>
                      <div className="mt-4 flex flex-wrap items-baseline gap-1">
                        <span
                          className={`font-display ${priceSize} font-black tracking-tighter`}
                          data-testid={`plan-price-${p.id}`}
                        >
                          {priceStr}
                        </span>
                        <span className="text-ink-muted text-sm">
                          {p.price === 0 ? (p.priceSuffix || "forever") : "/ mo"}
                        </span>
                        {strikeMonthly != null && (
                          <span className="ml-1 text-ink-muted text-sm line-through" data-testid={`plan-strike-${p.id}`}>
                            {fx(strikeMonthly)}
                          </span>
                        )}
                      </div>
                      {annualTotal != null && (
                        <div className="mt-1 text-[11px] text-emerald-300 font-mono uppercase tracking-wider" data-testid={`plan-annual-${p.id}`}>
                          {fx(annualTotal)}/yr · save {fx(p.price * 12 - annualTotal)}
                        </div>
                      )}
                    </>
                  );
                })()}
                {p.subtitle && (
                  <div className="mt-2 text-[12px] text-ink-secondary italic">
                    {p.subtitle}
                  </div>
                )}
                <div className="mt-5 space-y-4 flex-1">
                  {(p.groups || []).map((group, gi) => (
                    <div key={gi}>
                      {group.label && (
                        <div className="font-mono text-[10px] uppercase tracking-wider text-cyan-300 mb-2">
                          // {group.label}
                        </div>
                      )}
                      <ul className="space-y-2">
                        {group.features.map((f) => (
                          <li key={f} className="flex items-start gap-2 text-sm text-ink-secondary">
                            <Check className="h-4 w-4 text-brand-amber mt-0.5 shrink-0" />
                            <span>{f}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/login?next=${encodeURIComponent(`/dashboard/billing?tab=plan&plan=${p.id}&cycle=${billingCycle}`)}`}
                  data-testid={`plan-cta-${p.id}`}
                  onClick={() => trackCta(`plan-${p.id}`)}
                  className={`mt-8 block text-center rounded-md py-2.5 font-semibold transition ${
                    p.highlight
                      ? "bg-brand-amber text-black hover:bg-brand-amberHover amber-glow"
                      : p.id === "free"
                      ? "border border-cyan-500/40 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20"
                      : p.id === "network"
                      ? "border border-brand-blue/50 bg-brand-blue/10 text-brand-blue hover:bg-brand-blue/20"
                      : "border border-line bg-bg-elevated text-ink-primary hover:bg-bg-surface"
                  }`}
                >
                  {p.ctaLabel || "Get started"}
                </Link>
                {p.highlight && proCount != null && (
                  <div
                    data-testid={`plan-social-proof-${p.id}`}
                    className="mt-3 text-center font-mono text-[10px] uppercase tracking-wider text-brand-amber/80"
                  >
                    Chosen by <span className="text-brand-amber font-bold">{proCount.toLocaleString()}</span> stations this month →
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Compare all plans — expandable table (Feb 2026 spec) */}
          <div style={{ textAlign: "center", margin: "32px 0 0" }}>
            <button
              type="button"
              onClick={() => {
                setShowCompare((s) => !s);
                trackCta(showCompare ? "compare-plans-hide" : "compare-plans-show");
              }}
              data-testid="compare-plans-toggle"
              aria-expanded={showCompare}
              aria-controls="compare-plans-wrapper"
              style={{
                fontSize: "13px",
                fontWeight: 500,
                padding: "10px 24px",
                borderRadius: "8px",
                border: "0.5px solid rgba(255,255,255,0.15)",
                background: "transparent",
                color: "#c8ccd8",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {showCompare ? "Hide comparison" : "Compare all plans"}
              <span style={{ fontSize: "11px" }}>{showCompare ? "↑" : "↓"}</span>
            </button>
            <div
              className="md:hidden"
              data-testid="compare-plans-mobile-hint"
              style={{ marginTop: "8px", fontSize: "11px", color: "#4a5068" }}
            >
              Scroll right to see all plans →
            </div>
          </div>

          <div id="compare-plans-wrapper">
            <CompareTable open={showCompare} trackCta={trackCta} />
          </div>
        </div>
      </section>

      {/* Brand tagline banner — full-width hero image with brand promise */}
      <section className="border-t border-line bg-black" data-testid="landing-tagline-banner">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20 flex flex-col items-center">
          <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.32em] text-cyan-300 mb-6">
            // THE COMPLETE OS FOR ON-AIR
          </div>
          <img
            src="/brand/broadcastflowai-tagline.png"
            alt="BroadcastFlow AI — The Complete AI Operating System for Radio Stations"
            className="w-full max-w-3xl h-auto object-contain rounded-2xl shadow-2xl shadow-brand-amber/10 ring-1 ring-white/5"
            data-testid="landing-tagline-image"
          />
          <p className="mt-8 text-center text-ink-secondary text-sm sm:text-base max-w-2xl">
            Ingest. Generate. Voice. Mix. Render. Schedule. Publish — all from one console built for broadcasters.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center gap-3" data-testid="tagline-cta-row">
            <Link
              to="/login"
              data-testid="tagline-cta-trial"
              onClick={() => trackCta("tagline-trial")}
              className="group inline-flex items-center gap-2 rounded-md bg-brand-amber px-6 py-3 text-sm font-semibold text-black amber-glow transition hover:-translate-y-0.5"
            >
              Start your 14-day free trial
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </Link>
            <a
              href="#pricing"
              data-testid="tagline-cta-pricing"
              onClick={() => trackCta("tagline-pricing")}
              className="inline-flex items-center gap-2 rounded-md border border-line bg-bg-elevated px-5 py-3 text-sm text-ink-secondary hover:text-ink-primary hover:border-cyan-500/50 transition"
            >
              Compare plans
            </a>
          </div>
          <div className="mt-3 text-[11px] font-mono uppercase tracking-wider text-ink-muted">
            No credit card required · cancel anytime
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-line bg-bg-surface py-12">
        <div className="mx-auto max-w-7xl px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/brand/broadcastflowai-logo.png" alt="BroadcastFlow AI" className="h-12 w-auto object-contain" />
            <span className="text-ink-muted text-xs">© 2026 — Built for broadcasters</span>
          </div>
          <div className="flex flex-wrap items-center gap-5 text-xs font-mono uppercase tracking-wider text-ink-muted">
            <Link to="/terms" className="hover:text-brand-amber transition" data-testid="footer-terms">Terms</Link>
            <Link to="/privacy" className="hover:text-brand-amber transition" data-testid="footer-privacy">Privacy</Link>
            <Link to="/refunds" className="hover:text-brand-amber transition" data-testid="footer-refunds">Refunds</Link>
            <Link to="/cookies" className="hover:text-brand-amber transition" data-testid="footer-cookies">Cookies</Link>
            <Link to="/contact" className="hover:text-brand-amber transition" data-testid="footer-contact">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
